/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import first from "lodash/first";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface BookingSliceState {
  appointmentTypeId?: string;
  currentStep?: BookingStep;
  isConfirmationAlertOpen: boolean;
  optionalAttendees?: BookingAttendee[];
  patientId?: string;
  providerType?: BookingProviderType;
  recurrence?: EventRecurrence;
  reschedulingAppointmentData?: AppointmentDetailResponse;
  timeRange?: BookingTimeRange;
}

const initialState: BookingSliceState = {
  isConfirmationAlertOpen: false,
};

const bookingSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "booking",
  reducers: {
    resetBookingState: () => initialState,
    setAppointmentTypeId: (state, action: PayloadAction<string>) => {
      state.appointmentTypeId = action.payload;
    },
    setIsConfirmationAlertOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationAlertOpen = action.payload;
    },
    setNextStep: (state, action: PayloadAction<BookingStep>) => {
      state.currentStep = action.payload;
    },
    setOptionalAttendees: (state, action: PayloadAction<BookingAttendee[]>) => {
      state.optionalAttendees = action.payload;
    },
    setPatientId: (state, action: PayloadAction<string>) => {
      state.patientId = action.payload;
    },
    setProviderType: (state, action: PayloadAction<BookingProviderType>) => {
      state.providerType = action.payload;
      state.appointmentTypeId = undefined;
      state.optionalAttendees = undefined;
      state.recurrence = undefined;
    },
    setRecurrence: (state, action: PayloadAction<EventRecurrence>) => {
      state.recurrence = action.payload;
    },
    setReschedulingAppointmentData: (
      state,
      action: PayloadAction<AppointmentDetailResponse>,
    ) => {
      state.reschedulingAppointmentData = action.payload;
    },

    setTimeRange: (state, action: PayloadAction<BookingTimeRange>) => {
      state.timeRange = action.payload;
    },
    showConfirmationAlert: (state) => {
      state.isConfirmationAlertOpen = true;
    },
  },
});

const {
  resetBookingState: resetBookingStateAction,
  setAppointmentTypeId: setAppointmentTypeIdAction,
  setIsConfirmationAlertOpen: setIsConfirmationAlertOpenAction,
  setNextStep: setNextStepAction,
  setOptionalAttendees: setOptionalAttendeesAction,
  setPatientId: setSelectedPatientIdAction,
  setProviderType: setProviderTypeAction,
  setRecurrence: setRecurrenceAction,
  setReschedulingAppointmentData: setReschedulingAppointmentDataAction,
  setTimeRange: setTimeRangeAction,
} = bookingSlice.actions;

const selectIsConfirmationAlertOpen = (state: RootState) =>
  state.booking.isConfirmationAlertOpen;
const selectBookingStep = (state: RootState) => state.booking.currentStep;
const selectRecurrence = (state: RootState) => state.booking.recurrence;
const selectProviderType = (state: RootState) => state.booking.providerType;
const selectTimeRange = (state: RootState) => state.booking.timeRange;
const selectOptionalAttendees = (state: RootState) =>
  state.booking.optionalAttendees;

const selectAppointmentTypeGroup = (state: RootState) => {
  const appointmentType = first(state.booking.providerType?.appointmentTypes);
  return appointmentType?.appointmentTypeGroup;
};

const selectAppointmentTypeLabel = (state: RootState) => {
  const appointmentType = state.booking.providerType?.appointmentTypes?.find(
    (a) => a.appointmentTypeExtId === state.booking.appointmentTypeId,
  );
  return appointmentType?.appointmentTypeLabel;
};

const selectReschedulingAppointmentData = (state: RootState) =>
  state.booking.reschedulingAppointmentData;

const selectAppointmentTypeId = (state: RootState) =>
  state.booking.appointmentTypeId;

const selectAppointmentTypes = (state: RootState) =>
  state.booking.providerType?.appointmentTypes;
const selectAppointmentTitle = (state: RootState) =>
  state.booking.providerType?.providerType;

const selectPatientId = (state: RootState) => {
  if (state.appUser.isPatient) {
    return state.appUser.externalId;
  }

  return state.booking.patientId;
};

const selectInitialStep = (state: RootState): BookingStep => {
  if (state.appUser.isPatient) {
    return "provider";
  }

  return "patient";
};

const useBookingDispatch = () => {
  const dispatch = useAppDispatch();

  const setNextStep = (step: BookingStep) => {
    dispatch(setNextStepAction(step));
  };

  const setSelectedPatientId = (patientId: string) => {
    dispatch(setSelectedPatientIdAction(patientId));
  };

  const setProviderType = (providerType: BookingProviderType) => {
    dispatch(setProviderTypeAction(providerType));
  };

  const setRecurrence = (recurrence: EventRecurrence) => {
    dispatch(setRecurrenceAction(recurrence));
  };

  const setAppointmentTypeId = (appointmentTypeId: string) => {
    dispatch(setAppointmentTypeIdAction(appointmentTypeId));
  };

  const setOptionalAttendees = (attendees: BookingAttendee[]) => {
    dispatch(setOptionalAttendeesAction(attendees));
  };

  const showConfirmationAlert = () => {
    dispatch(setIsConfirmationAlertOpenAction(true));
  };

  const setReschedulingAppointmentData = (data: AppointmentDetailResponse) => {
    dispatch(setReschedulingAppointmentDataAction(data));
  };

  const closeConfirmationAlert = () => {
    dispatch(setIsConfirmationAlertOpenAction(false));
  };

  const resetBookingState = () => {
    dispatch(resetBookingStateAction());
  };

  const setTimeRange = (timeRange: BookingTimeRange) => {
    dispatch(setTimeRangeAction(timeRange));
  };

  return {
    closeConfirmationAlert,
    resetBookingState,
    setAppointmentTypeId,
    setNextStep,
    setOptionalAttendees,
    setProviderType,
    setRecurrence,
    setReschedulingAppointmentData,
    setSelectedPatientId,
    setTimeRange,
    showConfirmationAlert,
  };
};

export {
  initialState,
  resetBookingStateAction,
  selectAppointmentTitle,
  selectAppointmentTypeGroup,
  selectAppointmentTypeId,
  selectAppointmentTypeLabel,
  selectAppointmentTypes,
  selectBookingStep,
  selectInitialStep,
  selectIsConfirmationAlertOpen,
  selectOptionalAttendees,
  selectPatientId,
  selectProviderType,
  selectRecurrence,
  selectReschedulingAppointmentData,
  selectTimeRange,
  useBookingDispatch,
};

export type { BookingSliceState };
export default bookingSlice.reducer;
